import {type FormEvent, type ReactNode, useState} from "react";

import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {Loader2Icon, MoreHorizontalIcon, PencilIcon, PlusIcon, Trash2Icon} from "lucide-react";
import {toast} from "sonner";

import {type OsImage, osImagesCreate, osImagesDelete, osImagesList, osImagesUpdate} from "@/sdk";

import {Badge} from "@/components/ui/badge";
import {Button} from "@/components/ui/button";
import {Card, CardContent} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {Input} from "@/components/ui/input";
import {Label} from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const QK = ["admin", "os-images"] as const;

type ImgForm = {
  name: string;
  slug: string;
  url: string;
  family: string;
  version: string;
  arch: string;
  description: string;
  cloud_init_supported: boolean;
  is_active: boolean;
};

const empty: ImgForm = {
  name: "",
  slug: "",
  url: "",
  family: "",
  version: "",
  arch: "x86_64",
  description: "",
  cloud_init_supported: true,
  is_active: true,
};

export const AdminOSImages = () => {
  const qc = useQueryClient();
  const [openForm, setOpenForm] = useState<{mode: "create" | "edit"; img?: OsImage} | null>(null);
  const [form, setForm] = useState<ImgForm>(empty);
  const [deleteTarget, setDeleteTarget] = useState<OsImage | null>(null);

  const query = useQuery({
    queryKey: QK,
    queryFn: async () => {
      const {data, error} = await osImagesList();
      if (error) throw error;
      return (data?.items ?? []).slice().sort((a, b) => a.display_order - b.display_order);
    },
  });

  const invalidate = () => qc.invalidateQueries({queryKey: QK});

  const createM = useMutation({
    mutationFn: () =>
      osImagesCreate({
        body: {
          name: form.name,
          slug: form.slug,
          url: form.url,
          family: form.family || undefined,
          version: form.version || undefined,
          arch: form.arch || undefined,
          description: form.description || undefined,
          cloud_init_supported: form.cloud_init_supported,
          is_active: form.is_active,
        },
      }),
    onSuccess: ({error}) => {
      if (error) {
        toast.error(extractError(error));
        return;
      }
      toast.success("OS image created");
      setOpenForm(null);
      invalidate();
    },
  });

  const updateM = useMutation({
    mutationFn: () =>
      osImagesUpdate({
        path: {id: openForm!.img!.id},
        body: {
          name: form.name || undefined,
          url: form.url || undefined,
          family: form.family || undefined,
          version: form.version || undefined,
          arch: form.arch || undefined,
          description: form.description || undefined,
          cloud_init_supported: form.cloud_init_supported,
          is_active: form.is_active,
        },
      }),
    onSuccess: ({error}) => {
      if (error) {
        toast.error(extractError(error));
        return;
      }
      toast.success("OS image updated");
      setOpenForm(null);
      invalidate();
    },
  });

  const deleteM = useMutation({
    mutationFn: (id: string) => osImagesDelete({path: {id}}),
    onSuccess: ({error}) => {
      if (error) {
        toast.error(extractError(error));
        return;
      }
      toast.success("OS image deleted");
      setDeleteTarget(null);
      invalidate();
    },
  });

  const startCreate = () => {
    setForm(empty);
    setOpenForm({mode: "create"});
  };

  const startEdit = (img: OsImage) => {
    setForm({
      name: img.name,
      slug: img.slug,
      url: img.url,
      family: img.family ?? "",
      version: img.version ?? "",
      arch: img.arch ?? "x86_64",
      description: img.description ?? "",
      cloud_init_supported: img.cloud_init_supported,
      is_active: img.is_active,
    });
    setOpenForm({mode: "edit", img});
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (openForm?.mode === "create") createM.mutate();
    else updateM.mutate();
  };

  const images = query.data ?? [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold">OS images</h1>
          <p className="text-sm text-muted-foreground">
            Bootable images customers can pick when creating servers.
          </p>
        </div>
        <Button onClick={startCreate}>
          <PlusIcon /> Add image
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {query.isLoading ? (
            <div className="flex items-center justify-center p-12 text-muted-foreground">
              <Loader2Icon className="size-5 animate-spin" />
            </div>
          ) : query.isError ? (
            <div className="p-6 text-sm text-destructive">Failed to load OS images.</div>
          ) : images.length === 0 ? (
            <div className="p-6 text-sm text-muted-foreground">No OS images defined.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Image</TableHead>
                  <TableHead>Family / Version</TableHead>
                  <TableHead>Arch</TableHead>
                  <TableHead>Active</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {images.map((img) => (
                  <TableRow key={img.id}>
                    <TableCell>
                      <div className="font-medium">{img.name}</div>
                      <div className="font-mono text-xs text-muted-foreground">{img.slug}</div>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {img.family} {img.version}
                    </TableCell>
                    <TableCell>
                      <Badge variant="muted">{img.arch}</Badge>
                    </TableCell>
                    <TableCell>
                      {img.is_active ? (
                        <Badge variant="success">active</Badge>
                      ) : (
                        <Badge variant="muted">inactive</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon-sm">
                            <MoreHorizontalIcon />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onSelect={() => startEdit(img)}>
                            <PencilIcon /> Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            variant="destructive"
                            onSelect={() => setDeleteTarget(img)}
                          >
                            <Trash2Icon /> Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={openForm !== null} onOpenChange={(o) => !o && setOpenForm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {openForm?.mode === "create" ? "Create OS image" : "Edit OS image"}
            </DialogTitle>
            <DialogDescription>
              Provide the image URL and metadata. Slug is immutable after creation.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <Field id="name" label="Name">
                <Input
                  id="name"
                  required
                  value={form.name}
                  onChange={(e) => setForm({...form, name: e.target.value})}
                />
              </Field>
              <Field id="slug" label="Slug">
                <Input
                  id="slug"
                  required
                  disabled={openForm?.mode === "edit"}
                  value={form.slug}
                  onChange={(e) => setForm({...form, slug: e.target.value})}
                />
              </Field>
            </div>
            <Field id="url" label="Image URL">
              <Input
                id="url"
                type="url"
                required
                placeholder="https://..."
                value={form.url}
                onChange={(e) => setForm({...form, url: e.target.value})}
              />
            </Field>
            <div className="grid grid-cols-3 gap-3">
              <Field id="family" label="Family">
                <Input
                  id="family"
                  placeholder="debian"
                  value={form.family}
                  onChange={(e) => setForm({...form, family: e.target.value})}
                />
              </Field>
              <Field id="version" label="Version">
                <Input
                  id="version"
                  placeholder="12"
                  value={form.version}
                  onChange={(e) => setForm({...form, version: e.target.value})}
                />
              </Field>
              <Field id="arch" label="Arch">
                <Input
                  id="arch"
                  value={form.arch}
                  onChange={(e) => setForm({...form, arch: e.target.value})}
                />
              </Field>
            </div>
            <Field id="description" label="Description">
              <Input
                id="description"
                value={form.description}
                onChange={(e) => setForm({...form, description: e.target.value})}
              />
            </Field>
            <div className="flex flex-wrap gap-4 text-sm">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={form.cloud_init_supported}
                  onChange={(e) =>
                    setForm({...form, cloud_init_supported: e.target.checked})
                  }
                />
                Cloud-init supported
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) => setForm({...form, is_active: e.target.checked})}
                />
                Active (visible to customers)
              </label>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpenForm(null)}>
                Cancel
              </Button>
              <Button type="submit" disabled={createM.isPending || updateM.isPending}>
                {openForm?.mode === "create"
                  ? createM.isPending
                    ? "Creating…"
                    : "Create image"
                  : updateM.isPending
                    ? "Saving…"
                    : "Save changes"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteTarget !== null} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete OS image?</DialogTitle>
            <DialogDescription>
              Soft-deletes <span className="font-mono">{deleteTarget?.slug}</span>. Existing
              servers built from it remain bootable.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={deleteM.isPending}
              onClick={() => deleteTarget && deleteM.mutate(deleteTarget.id)}
            >
              {deleteM.isPending ? "Deleting…" : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

function Field({id, label, children}: {id: string; label: string; children: ReactNode}) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      {children}
    </div>
  );
}

function extractError(err: unknown): string {
  return (err as {detail?: string} | undefined)?.detail ?? "Request failed";
}
